import { Link } from "wouter";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { breadcrumbSchema, personSchema, priapismCaseScholarlyArticle, webPageSchema } from "@/lib/seo";

interface PressItem {
  date: string;
  outlet: string;
  title: string;
  href: string;
}

const PRESS: PressItem[] = [
  {
    date: "2024",
    outlet: "Top Doctor Magazine",
    title: "Mental Health Doesn't Discriminate, An Interview with Dr. Shariq Refai",
    href: "https://topdoctormagazine.com/mental-health/mental-health-does-not-discriminate-an-interview-with-dr-shariq-refai/",
  },
  {
    date: "2024",
    outlet: "Duval County Medical Society",
    title: "Full Circle Calling, Spotlight on DCMS Member Shariq Refai, MD, MBA, FAPA",
    href: "https://www.dcmsonline.org/news/700524/Full-Circle-Calling-Spotlight-on-DCMS-Member-Shariq-Refai-MD-MBA-FAPA.htm",
  },
  {
    date: "Ongoing",
    outlet: "Forbes Business Council",
    title: "Council Member Profile and Contributions",
    href: "https://councils.forbes.com",
  },
  {
    date: "Ongoing",
    outlet: "SoMeDocs (Doctors on Social Media)",
    title: "Shrink Wrapped with Dr. Refai, video series",
    href: "https://doctorsonsocialmedia.com/shrink-wrapped-with-dr-refai/",
  },
  {
    date: "2025",
    outlet: "MedStory Podcast",
    title: "The Mental Health Emergency No One Can Escape",
    href: "https://podcasts.apple.com/us/podcast/the-mental-health-emergency-no-one-can-escape/id1804185340?i=1000727211753",
  },
  {
    date: "2026",
    outlet: "FL Top Docs",
    title: "FL Top Doc, Psychiatry, April 2026",
    href: "https://fltopdocs.com/april-2026/",
  },
  {
    date: "2025",
    outlet: "FL Top Docs",
    title: "FL Top Doc, Psychiatry, May 2025",
    href: "https://fltopdocs.com/fl-top-docs-proudly-presents-may-2025-approved-providers/",
  },
  {
    date: "2024",
    outlet: "FL Top Docs",
    title: "FL Top Doc, Psychiatry, April 2024",
    href: "https://fltopdocs.com/april-2024/",
  },
  {
    date: "2012",
    outlet: "Primary Care Companion for CNS Disorders",
    title:
      "A Case of Priapism Associated With Rapid Increase in Risperidone Dose, peer reviewed letter to the editor (with Nakama HH)",
    href: "https://www.psychiatrist.com/pcc/case-priapism-associated-rapid-increase-risperidone/",
  },
];

const SPEAKING_TOPICS = [
  "Why overthinking doesn't yield to insight, and what does work.",
  "The four-move pattern (Pattern, Pause, Reframe, Act) for working with anxiety loops.",
  "The high-performer pattern: why the people who look most put together are often quietly burning out.",
  "Modern psychiatry: what the field actually does, what gets it wrong, and where it's heading.",
  "Telepsychiatry and access: how the math works, where it doesn't, and what's at stake.",
  "The Shrink Network: how a coordinated mental health ecosystem actually gets built.",
  "Sports and performance psychiatry: anxiety, focus, and the athletes who carry it.",
];

const CREDENTIALS = [
  "Board certification: Psychiatry (American Board of Psychiatry and Neurology). Sports and performance psychiatry.",
  "Fellow of the American Psychiatric Association (FAPA).",
  "MD, MBA.",
  "NPI: 1467680660.",
  "ORCID: 0009-0009-1090-4373.",
  "Wikidata: Q139822307.",
  "Forbes Business Council member.",
];

const QUOTES = [
  "Overthinking isn't a lack of insight. The articles you've read are accurate. The advice you've gotten is reasonable. The reason it hasn't worked is that overthinking has a pattern, and patterns don't yield to information. They yield to structure.",
  "The wellness vocabulary is welcoming but vague. The clinical vocabulary is precise but unwelcoming. The job is to write in the space in between, accurately, in language a real person can use.",
  "Telepsychiatry isn't a workaround. For most adult outpatient psychiatric conditions, it's clinically equivalent to in-person care, and it makes the math work for patients who couldn't otherwise get to a clinic.",
  "There are no testimonials on this page yet. shrinQ is new, and inventing them isn't something I'll do. What's real is the method, the books behind it, and the psychiatrist who wrote them.",
];

export default function Press() {
  const TITLE = "Press Coverage of Shariq Refai, MD, MBA, Psychiatrist";
  const DESCRIPTION =
    "Press resources, media coverage, speaking topics, headshots, and contact for Shariq Refai, MD, MBA. Board-certified psychiatrist, author, and founder.";

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Press", path: "/press" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/press"
        schema={[
          personSchema,
          webPageSchema({
            path: "/press",
            title: TITLE,
            description: DESCRIPTION,
            type: "CollectionPage",
            breadcrumbs: crumbs,
          }),
          breadcrumbSchema(crumbs),
          priapismCaseScholarlyArticle,
        ]}
      />

      <article className="container mx-auto max-w-3xl px-4 md:px-8 pt-24 pb-16 md:pt-32 md:pb-24">
        <Breadcrumbs items={crumbs.map((c) => ({ name: c.name, href: c.path }))} />
        <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
          Press
        </span>
        <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight mt-6 mb-4">
          Press Coverage &amp; Media Features
        </h1>
        <p className="text-lg text-secondary font-light leading-relaxed mb-12">
          Bios, credentials, speaking topics, headshots, coverage, and how to get
          in touch.
        </p>

        {/* Bios */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">
            Short bio (50 words)
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Shariq Refai, MD, MBA is a board-certified psychiatrist, founder of
            the multistate telepsychiatry practice shrinkMD, founder of the
            educational program shrinQ, and the medical editor behind The Shrink
            Network. He's the author of three books on overthinking, anxiety, and
            the patterns that shape modern life.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">
            Medium bio (120 words)
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Shariq Refai, MD, MBA is a board-certified psychiatrist with dual
            board certification in psychiatry and in sports and performance
            psychiatry. He founded shrinkMD, a multistate telepsychiatry practice,
            and created the educational program shrinQ. He's the medical editor
            for Shrinkopedia, AnxietyResource, DepressionResource, AnxietyResearch,
            and PsychiatryRx, and the author of three books on overthinking,
            anxiety, and the patterns that quietly run modern lives. His clinical
            work has included affiliations involving the NFL Substance Abuse
            Program and the Jacksonville Jaguars. He's a Fellow of the American
            Psychiatric Association and a member of the Forbes Business Council. He
            writes from Jacksonville, Florida.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">
            Long bio (250 words)
          </h2>
          <div className="space-y-5 text-secondary font-light leading-relaxed">
            <p>
              Shariq Refai, MD, MBA is a board-certified psychiatrist with dual
              board certification in psychiatry and in sports and performance
              psychiatry, and more than fifteen years in clinical practice. He
              founded shrinkMD, an independent multistate telepsychiatry practice,
              to make psychiatric care easier to access. He created the
              educational program shrinQ as a structured way for adults to work
              with overthinking, rumination, and anxiety loops between or instead
              of clinical visits.
            </p>
            <p>
              He serves as medical editor for the editorial publications in The
              Shrink Network: Shrinkopedia, the clinically reviewed mental health
              encyclopedia; AnxietyResource and DepressionResource, the practical
              condition-specific education sites; AnxietyResearch, the editorial
              research and data layer; and PsychiatryRx, the plain-language
              psychiatric medication reference. He's also the founder of the
              wellness apps Unstuck and QuitScrolling.
            </p>
            <p>
              His professional background includes affiliations involving the NFL
              Substance Abuse Program and the Jacksonville Jaguars. He's a Fellow
              of the American Psychiatric Association (FAPA) and a member of the
              Forbes Business Council. He holds an MD and an MBA.
            </p>
            <p>
              He's the author of three books, including Your Mind Is Full of Sh*t:
              A Psychiatrist's Guide to Understanding Overthinking, Anxiety, and
              the Lies in Your Head; its companion Workbook; and The Havoc in Your
              Head: A Psychiatrist's Reset for Anxiety, Overthinking, Panic, and
              the Thoughts That Won't Let Go.
            </p>
            <p>He lives in Jacksonville, Florida.</p>
          </div>
        </section>

        {/* Credentials */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">Credentials</h2>
          <ul className="list-disc pl-6 space-y-2 text-secondary font-light leading-relaxed">
            {CREDENTIALS.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </section>

        {/* Speaking topics */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">Speaking topics</h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            I speak on the following topics regularly:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-secondary font-light leading-relaxed">
            {SPEAKING_TOPICS.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </section>

        {/* Media topics */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">Media topics</h2>
          <p className="text-secondary font-light leading-relaxed">
            I'm available for interviews on anxiety, panic, OCD-spectrum
            conditions, overthinking, the high-performer pattern, modern
            psychiatry, telepsychiatry, evidence-based mental health, the limits
            of popular mental health commentary, mental health literacy, sports
            and performance psychiatry, and the ethics and clinical realities of
            digital mental health.
          </p>
        </section>

        {/* Quotes */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">
            Quotes I'm comfortable being attributed to
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-5">
            These are short positions I'll stand behind in print:
          </p>
          <div className="space-y-6">
            {QUOTES.map((q, i) => (
              <blockquote
                key={i}
                className="border-l-2 border-accent pl-5 text-primary font-serif italic leading-relaxed"
              >
                {q}
              </blockquote>
            ))}
          </div>
        </section>

        {/* Featured coverage */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-6">
            Featured coverage
          </h2>
          <ul className="list-none p-0 grid gap-8 border-t border-border">
            {PRESS.map((p, i) => (
              <li key={i} className="border-b border-border pb-8 flex flex-col gap-2">
                <span className="text-xs font-semibold tracking-widest uppercase text-accent">
                  {p.date} &middot; {p.outlet}
                </span>
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl text-primary hover:text-accent transition-colors"
                >
                  {p.title}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Press contact */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif text-primary mb-4">Press contact</h2>
          <p className="text-secondary font-light leading-relaxed">
            Email:{" "}
            <a
              href="mailto:media@shariqrefai.com"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              media@shariqrefai.com
            </a>
          </p>
          <p className="text-secondary font-light leading-relaxed mt-2">
            Contact form:{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              /contact
            </Link>{" "}
            with "Media &amp; Press" selected.
          </p>
        </section>

        {/* Headshots and assets */}
        <section className="mb-4">
          <h2 className="text-2xl font-serif text-primary mb-4">
            Headshots and assets
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Downloadable assets for editorial use:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-secondary font-light leading-relaxed">
            <li>
              <a
                href="/img/headshot-large.jpg"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Headshot, print (1400x1400 JPG, 190 KB)
              </a>
            </li>
            <li>
              <a
                href="/img/headshot-1200.webp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Headshot, large web (1200x1200 WebP, 93 KB)
              </a>
            </li>
            <li>
              <a
                href="/img/headshot.webp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Headshot, standard web (800x800 WebP, 46 KB)
              </a>
            </li>
            <li>
              <a
                href="/img/headshot-400.webp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Headshot, thumbnail (400x400 WebP, 17 KB)
              </a>
            </li>
            <li>
              <a
                href="/img/speaking-photo-large.jpg"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Speaking photo, print (1200x1800 JPG, 182 KB)
              </a>
            </li>
            <li>
              <a
                href="/img/speaking-photo.webp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Speaking photo, web (1000x1500 WebP, 74 KB)
              </a>
            </li>
            <li>
              <a
                href="/og-home.jpg"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Open graph image (og-home.jpg)
              </a>
            </li>
          </ul>
          <p className="text-sm text-muted-foreground font-light leading-relaxed mt-3">
            All images approved for editorial use with credit to Shariq Refai, MD, MBA. For high-resolution originals or additional formats, use the press contact above.
          </p>
        </section>
      </article>

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            variant="dark"
            eyebrow="Continue"
            cards={[
              { label: "Read the founder narrative", href: "/founder-of-the-shrink-network" },
              { label: "See the project list", href: "/projects" },
              { label: "Book speaking", href: "/speaking" },
              { label: "See the books", href: "/books" },
            ]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
